import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, Search, Clock, LayoutGrid, MousePointerClick, PencilLine, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../ui/sheet';
import { Button } from '../../ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { ActivityTypeBadge, QueryChips, StatusCodeBadge, formatActivityDate, formatDuration, downloadCsv } from './activityShared';

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="rounded-lg border px-3 py-2 flex items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
    <Icon className="h-4 w-4 text-orange-500 shrink-0" />
    <div className="leading-tight">
      <div className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-muted-color)' }}>{label}</div>
      <div className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>{value}</div>
    </div>
  </div>
);

export const SessionActivitySheet = ({ sessionId, open, onOpenChange }) => {
  const { token, backendUrl } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('asc');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!open || !sessionId || !token) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ per_page: 2000, sort });
    if (search) params.append('search', search);
    if (type !== 'all') params.append('type', type);
    const t = setTimeout(() => {
      axios.get(`${backendUrl}/api/audit/access-logs/${sessionId}/activity?${params}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setData(r.data))
        .catch((e) => setError(e.response?.data?.detail || 'Failed to load session activity'))
        .finally(() => setLoading(false));
    }, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [open, sessionId, token, backendUrl, search, type, sort, refreshTick]);

  useEffect(() => { if (!open) { setSearch(''); setType('all'); setSort('asc'); setData(null); } }, [open]);

  const items = data?.items || [];
  const summary = data?.summary;
  const login = data?.login;
  const pagesLabel = useMemo(() => (summary?.pages || []).map((p) => `${p.label} (${p.count})`).join(', '), [summary]);

  const exportCsv = () => downloadCsv(
    `session_${sessionId.slice(0, 8)}_activity.csv`,
    ['Timestamp', 'Type', 'Menu', 'Action', 'Query', 'Target', 'Status', 'Duration (ms)'],
    items.map((i) => [formatActivityDate(i.timestamp), i.type, i.page_label || '', i.action, Object.entries(i.query || {}).map(([k, v]) => `${k}=${v}`).join(' '), i.target_id || '', i.status_code, i.duration_ms])
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="session-activity-sheet" className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle style={{ color: 'var(--text-color)' }}>Login session detail</SheetTitle>
          <SheetDescription>
            {login ? <>{login.user_name} · {login.email} · signed in {formatActivityDate(login.timestamp)} from {login.ip_address}</> : 'Menus visited and queries run during this login'}
          </SheetDescription>
        </SheetHeader>

        {loading && !data ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
        ) : error ? (
          <div data-testid="session-activity-error" className="mt-6 text-sm text-red-600">{error}</div>
        ) : (
          <div className="mt-5 space-y-5">
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Stat icon={Clock} label="Active time" value={formatDuration(summary.duration_seconds)} testId="session-stat-duration" />
                <Stat icon={LayoutGrid} label="Menus" value={summary.pages.length} testId="session-stat-pages" />
                <Stat icon={MousePointerClick} label="Queries" value={summary.queries} testId="session-stat-queries" />
                <Stat icon={PencilLine} label="Changes" value={summary.changes} testId="session-stat-changes" />
              </div>
            )}
            {summary?.pages?.length > 0 && (
              <div data-testid="session-pages-visited" className="text-xs" style={{ color: 'var(--text-muted-color)' }}>
                <span className="font-semibold">Menus visited:</span> {pagesLabel}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input data-testid="session-activity-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by menu, action or query value…"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <select data-testid="session-activity-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg">
                <option value="all">All types</option>
                <option value="page_view">Menu visits</option>
                <option value="query">Queries</option>
                <option value="change">Changes</option>
              </select>
              <Button data-testid="session-activity-sort-date" variant="outline" onClick={() => setSort((s) => (s === 'asc' ? 'desc' : 'asc'))} className="flex items-center gap-1" title={sort === 'asc' ? 'Oldest first — click for newest first' : 'Newest first — click for oldest first'}>
                {sort === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />} Date
              </Button>
              <Button data-testid="session-activity-refresh" variant="outline" onClick={() => setRefreshTick((t) => t + 1)} disabled={loading} className="flex items-center gap-1" title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button data-testid="session-activity-export" variant="outline" onClick={exportCsv} disabled={items.length === 0} className="flex items-center gap-2">
                <Download className="h-4 w-4" /> CSV
              </Button>
            </div>

            {items.length === 0 ? (
              <div data-testid="session-activity-empty" className="text-center py-10 text-sm" style={{ color: 'var(--text-muted-color)' }}>
                No activity recorded for this login{search || type !== 'all' ? ' with the current filter' : ''}.
              </div>
            ) : (
              <ol data-testid="session-activity-timeline" className="relative border-l pl-4 space-y-3" style={{ borderColor: 'var(--border-color)' }}>
                {items.map((it) => (
                  <li key={it.id} data-testid="session-activity-item" className="relative">
                    <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${it.type === 'change' ? 'bg-amber-500' : it.type === 'page_view' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                    <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--text-muted-color)' }}>
                      <span className="font-mono">{formatActivityDate(it.timestamp)}</span>
                      <ActivityTypeBadge type={it.type} />
                      {it.page_label && <span className="font-medium" style={{ color: 'var(--text-color)' }}>{it.page_label}</span>}
                      <StatusCodeBadge code={it.status_code} />
                      <span>{it.duration_ms} ms</span>
                    </div>
                    <div className="text-sm mt-0.5" style={{ color: 'var(--text-color)' }}>
                      {it.action}{it.target_id ? <span className="font-mono text-xs ml-1" style={{ color: 'var(--text-muted-color)' }}>{it.target_id}</span> : null}
                    </div>
                    <div className="mt-1"><QueryChips query={it.query} /></div>
                  </li>
                ))}
              </ol>
            )}
            {data && data.total > items.length && (
              <div className="text-xs text-center" style={{ color: 'var(--text-muted-color)' }}>Showing first {items.length} of {data.total} entries — refine the filter to see more.</div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SessionActivitySheet;
